import { Box, Button, Flex, Heading, Image, Text } from "@chakra-ui/react";
import Link from "next/link";
import { TbError404 } from "react-icons/tb";
import { useAppSelector } from "store/hooks";

/**
 * The 404 content (illustration + copy + CTA), customizable per tenant via
 * `layout_config.not_found_config`. Rendered inside `SiteChrome` so it always
 * carries the tenant's top/bottom navigation. Used by both the `/404` page and
 * the in-app not-found fallback in `withSiteConfig`.
 */
function NotFoundView() {
  const cfg = useAppSelector(
    (state) => state.config.layout_config?.not_found_config,
  );

  const title = cfg?.title || "Page not found";
  const message =
    cfg?.message ||
    "The page you’re looking for doesn’t exist or may have been moved. Use the navigation to find your way back.";
  const buttonLabel = cfg?.button_label || "Back to Home";
  const buttonHref = cfg?.button_href || "/";

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      minH={{ base: "60vh", xl: "70vh" }}
      px={6}
      py={12}
      gap={6}
    >
      <Box color={cfg?.icon_color}>
        {cfg?.image ? (
          <Image
            src={cfg.image}
            alt={title}
            maxH={{ base: "12rem", md: "16rem" }}
            objectFit="contain"
          />
        ) : cfg?.glyph ? (
          <Text fontSize={{ base: "5rem", md: "7rem" }} lineHeight="1">
            {cfg.glyph}
          </Text>
        ) : (
          <Box as={TbError404} fontSize={{ base: "7rem", md: "10rem" }} />
        )}
      </Box>

      <Box maxW="lg">
        <Heading size="lg" color={cfg?.title_color}>
          {title}
        </Heading>
        <Text mt={4} color={cfg?.message_color} opacity={cfg?.message_color ? 1 : 0.85}>
          {message}
        </Text>
      </Box>

      <Button as={Link} href={buttonHref} colorScheme="primary" size="lg">
        {buttonLabel}
      </Button>
    </Flex>
  );
}

export default NotFoundView;
